import { ChartTypes } from '@mode/shared/contract-common';

export const ELEMENT_SEPARATOR = '\x1f';
export const VALUE_SEPARATOR = '\x00';
export const TERMINATOR = '\x1e';

export interface FacetTargetItem {
  field: string;
  sourceType: string;
  value?: string | null;
}

export interface FacetSizeItem {
  target: FacetTargetItem[];
  width?: number;
  height?: number;
}

/**
 * Converts an array of FacetSizeItems as received from the Muze callback
 * into the FacetSizeMap that we use for persistence. Each item becomes
 * an entry in the array.
 */
export function encodeFacetSizeMap(items: FacetSizeItem[]) {
  let facetSizes: ChartTypes.FacetSizeMap = {};
  items.forEach((item) => {
    facetSizes[encodeKey(item.target)] = { width: item.width, height: item.height };
  });
  return facetSizes;
}

/**
 * Converts our persisted FacetSizeMap into an array of FacetSizeItems which
 * can be passed to Muze as part of the configuration
 */
export function decodeFacetSizeMap(facetSizes: Readonly<ChartTypes.FacetSizeMap> | undefined) {
  const sizes: FacetSizeItem[] = [];
  if (facetSizes !== undefined) {
    for (const [key, value] of Object.entries(facetSizes)) {
      let elem = {
        target: key.includes(ELEMENT_SEPARATOR) ? decodeKey(key) : parseKey(key),
        width: value.width,
        height: value.height,
      };
      sizes.push(elem);
    }
  }
  return sizes;
}

/**
 * Muze provides all of the facet size changes for either the rows or the columns,
 * but not both at the same time. Ex: if the user resizes a row header, then
 * Muze passes that change plus any other row header changes. Muze does not
 * include any column header changes. Conversely, if the user then resizes
 * a column header, Muze will only pass the column header changes and will not
 * include the row header changes.
 *
 * In Mode, we persist both the row and column changes in a single map.
 * Therefore, we need to merge any new changes with any previous changes from
 * the other positional channel.
 *
 * @param current Current map containing both row and column settings
 * @param update New map containing either the row or column settings
 */
export function mergeFacetSizeMaps(
  current: Readonly<ChartTypes.FacetSizeMap>,
  update: Readonly<ChartTypes.FacetSizeMap>
) {
  // Decode the new map to find the top level target field. Each 'target'
  // starts at the top level facet and goes down to the level of the resize.
  //
  const updateItems = decodeFacetSizeMap(update);
  const topLevelField = updateItems[0]?.target[0]?.field;
  if (topLevelField === undefined) {
    // If there is nothing in the map to merge, just return the original map.
    // Not sure this will ever happen.
    return current;
  }
  // Filter out the items from the current map that match the field from the update map.
  // This will just leave the items on the other positional channel which are not
  // part of this update.
  const otherChannelItems = decodeFacetSizeMap(current).filter(
    (item) => !item.target.some((target) => target.field === topLevelField)
  );
  // Return the merge of the complete and partial
  const otherChannelMap = encodeFacetSizeMap(otherChannelItems);
  return { ...otherChannelMap, ...update };
}

/**
 * Flattens the array of FacetTargetItems into a single string which
 * acts as a unique key to the location of the facet.
 */
function encodeKey(target: FacetTargetItem[]) {
  let key = '';
  target.forEach((element, i) => {
    key += JSON.stringify(element.field) + ELEMENT_SEPARATOR + JSON.stringify(element.sourceType);
    if (element.value !== undefined) {
      key += ELEMENT_SEPARATOR + (element.value === null ? VALUE_SEPARATOR : JSON.stringify(element.value));
    }
    if (i < target.length - 1) {
      key += TERMINATOR;
    }
  });
  return key;
}

/**
 * Restores the flattened key into the array of FacetTargetItems which
 * is needed for the Muze configuration.
 */
function decodeKey(key: string) {
  const target: FacetTargetItem[] = [];
  key.split(TERMINATOR).forEach((element) => {
    const values = element.split(ELEMENT_SEPARATOR);
    target.push({
      field: JSON.parse(values[0]),
      sourceType: JSON.parse(values[1]),
      value: values[2] === undefined ? undefined : values[2] === VALUE_SEPARATOR ? null : JSON.parse(values[2]),
    });
  });
  return target;
}

function parseKey(key: string) {
  return JSON.parse(key) as FacetTargetItem[];
}
